import { readCmtsSession } from "../auth/authStore.js";
import type { CmtsStoredSession } from "../auth/authStore.js";
import { resolveCmtsClearance, resolveCmtsCustomerScope, resolveCmtsDepartmentScope, resolveCmtsExternalPartyType, resolveCmtsPersona, resolveCmtsSupplierScope } from "./cmtsScopeResolver.js";
import type { CmtsSession, CmtsSessionResolution } from "./cmtsSessionTypes.js";
import { throwCmtsCanonicalError } from "../api/cmtsApiErrors.js";
import { normalizeCmtsRoles } from "../auth/normalizeCmtsRoles.js";
import { CMTS_ROLE_CODES } from "../auth/cmtsRoleCodes.js";

const TENANT_NAMES: Record<string, string> = {
  "TNT-SWOT-IN": "SWOT DigiNXT India",
  "TNT-DEMO-MFG": "Demo Manufacturing Group",
};

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0).map((item) => item.trim()) : [];
}

export function buildCmtsSession(stored: CmtsStoredSession | null): CmtsSessionResolution {
  if (!stored?.userId || !stored.tenantId) return { status: "missing", reason: "CMTS session or tenant is missing." };
  const roles = normalizeCmtsRoles([...normalizeStringArray(stored.roles), ...normalizeStringArray(stored.roleIds)]);
  const permissions = Array.from(new Set([...normalizeStringArray(stored.permissions), ...normalizeStringArray(stored.permissionCodes)]));
  const tenantCode = stored.tenantCode || stored.tenantId;
  const tenantName = stored.tenantName || TENANT_NAMES[tenantCode] || tenantCode;
  const organizationUnitIds = Array.from(new Set([...(stored.organizationUnitId ? [stored.organizationUnitId] : []), ...normalizeStringArray(stored.organizationUnitIds)]));
  const session: CmtsSession = {
    tenantId: stored.tenantId,
    tenantCode,
    tenantName,
    organizationUnitId: organizationUnitIds[0],
    organizationUnitIds,
    userId: stored.userId,
    displayName: stored.displayName || "CMTS User",
    email: stored.email || "",
    persona: stored.persona || resolveCmtsPersona(roles, stored.displayName || "CMTS User"),
    roleIds: roles,
    permissionCodes: permissions,
    departmentIds: resolveCmtsDepartmentScope({ ...stored, roles }),
    businessUnitIds: Array.from(new Set((stored.businessUnitIds ?? ["bu-quality", "bu-manufacturing"]).map(String))),
    siteIds: Array.from(new Set((stored.siteIds ?? ["site-chennai", "site-hosur"]).map(String))),
    customerIds: resolveCmtsCustomerScope({ ...stored, roles }),
    supplierIds: resolveCmtsSupplierScope({ ...stored, roles }),
    isExternalUser: Boolean(stored.isExternalUser || roles.includes(CMTS_ROLE_CODES.CUSTOMER_USER) || roles.includes(CMTS_ROLE_CODES.SUPPLIER_USER)),
    externalPartyType: stored.externalPartyType || resolveCmtsExternalPartyType(roles),
    confidentialityClearance: stored.confidentialityClearance || resolveCmtsClearance(roles),
    runtimeMode: stored.runtimeMode || "OFFLINE_DEMO",
  };
  return { status: "ready", session };
}

export function resolveCurrentCmtsSession(): CmtsSessionResolution {
  return buildCmtsSession(readCmtsSession());
}

export function requireCurrentCmtsSession(): CmtsSession {
  const resolved = resolveCurrentCmtsSession();
  if (resolved.status !== "ready") throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ACTOR", message: resolved.reason, details: { sessionStatus: resolved.status } });
  return resolved.session;
}
